/**
 * Single source of truth for the canvas chat's referenced entities — the merge of currently
 * selected canvas nodes and file-record text excerpts, with per-entity removal. Lifted out of
 * the chat body so the always-present composer bar can show selection pills *above* the input
 * (a floating overlay) while the same entities ride along on the sent message — one removal
 * state shared by both, rather than two diverging copies.
 *
 * Canvas-scoped (keyed by canvasId only) — no SessionProvider required, so it can run at the
 * floating-chat panel level.
 */

import { useFileRecordTextSelectionReferencedEntity } from "@/panes/file-record-detail/useFileRecordTextSelectionReferencedEntity";
import { mergeCanvasReferencedEntities } from "@/utils/mergeReferencedEntities";
import type { ReferencedEntity } from "@cohesive/api-client";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useCanvasSelectionMirror } from "../context/CanvasSelectionMirrorContext";
import { convertNodesToReferencedEntities } from "../utils/convertNodesToReferencedEntities";

export interface CanvasChatReferencedEntities {
  referencedEntities: ReferencedEntity[];
  onRemoveEntity: (entityId: string) => void;
  /** Drop the text-selection entities that just rode along on a sent message. */
  clearTextSelectionsFromMessage: (
    entities: ReferencedEntity[] | undefined,
  ) => void;
}

export function useCanvasChatReferencedEntities(
  canvasId: string,
): CanvasChatReferencedEntities {
  // Selected nodes are published by CanvasPage via CanvasSelectionMirrorContext.
  const selectedNodes = useCanvasSelectionMirror();

  const {
    textSelectionEntities,
    removeSelection,
    clearSelectionsFromMessage,
    isTextSelectionEntityId,
  } = useFileRecordTextSelectionReferencedEntity(canvasId);

  const canvasReferencedEntities = useMemo(
    () => convertNodesToReferencedEntities(selectedNodes),
    [selectedNodes],
  );

  const [removedEntityIds, setRemovedEntityIds] = useState<Set<string>>(
    new Set(),
  );
  const selectedNodeIds = useMemo(
    () => selectedNodes.map((n) => n.id).join(","),
    [selectedNodes],
  );
  useEffect(() => {
    setRemovedEntityIds(new Set());
  }, [selectedNodeIds]);

  const referencedEntities = useMemo(
    () =>
      mergeCanvasReferencedEntities(
        canvasReferencedEntities,
        textSelectionEntities,
        removedEntityIds,
      ),
    [canvasReferencedEntities, removedEntityIds, textSelectionEntities],
  );

  const onRemoveEntity = useCallback(
    (entityId: string) => {
      if (isTextSelectionEntityId(entityId)) {
        removeSelection(entityId);
        return;
      }
      setRemovedEntityIds((prev) => new Set([...prev, entityId]));
    },
    [removeSelection, isTextSelectionEntityId],
  );

  return {
    referencedEntities,
    onRemoveEntity,
    clearTextSelectionsFromMessage: clearSelectionsFromMessage,
  };
}
